/**
 * One cached dataset (e.g. "JobAssignment/List for 2026-09-30") with:
 * - reuse while younger than `maxAgeMs` (and requested after `freshAfter`, when given)
 * - one request at a time: callers asking for the same key share the in-flight request
 * - forced refreshes never overlap an older request: they wait for it, then fetch again
 * - abort only when every caller has gone. The abort is deferred by one tick, so a StrictMode
 *   unmount + remount (or an immediate re-subscribe) reuses the request instead of repeating it
 * - a new key (another day) aborts the previous key's request
 */
export interface ResourceEntry<T> {
  key: string
  value: T
  /** When the request that produced `value` was sent (conservative freshness). */
  requestedAt: number
}

export interface ResourceGetOptions {
  maxAgeMs: number
  /** Only accept data requested at/after this epoch ms (Refresh button, after a mutation). */
  freshAfter?: number
  signal?: AbortSignal
}

interface Flight<T> {
  key: string
  startedAt: number
  controller: AbortController
  promise: Promise<T>
  subscribers: number
  abortTimer: ReturnType<typeof setTimeout> | null
}

export const createAbortError = () => {
  const error = new Error('The request was cancelled.')
  error.name = 'AbortError'
  return error
}

export const isAbortError = (error: unknown) =>
  error instanceof Error && (error.name === 'AbortError' || error.name === 'CanceledError' || (error as { code?: string }).code === 'ERR_CANCELED')

export class SharedResource<T> {
  private entry: ResourceEntry<T> | null = null
  private flight: Flight<T> | null = null

  constructor(
    private readonly fetcher: (key: string, signal: AbortSignal) => Promise<T>,
    private readonly now: () => number = Date.now,
  ) {}

  /** Last value for `key` (fresh or stale), without fetching. */
  peek(key: string): ResourceEntry<T> | null {
    return this.entry?.key === key ? this.entry : null
  }

  isFresh(key: string, { maxAgeMs, freshAfter }: Pick<ResourceGetOptions, 'maxAgeMs' | 'freshAfter'>): boolean {
    const entry = this.peek(key)
    if (!entry) return false
    if (freshAfter != null && entry.requestedAt < freshAfter) return false
    return this.now() - entry.requestedAt < maxAgeMs
  }

  isInFlight(key: string): boolean {
    return this.flight?.key === key
  }

  async get(key: string, options: ResourceGetOptions): Promise<T> {
    const { signal, freshAfter } = options
    if (signal?.aborted) throw createAbortError()
    if (this.isFresh(key, options)) return (this.entry as ResourceEntry<T>).value

    const flight = this.flight
    if (flight && flight.key === key) {
      if (freshAfter == null || flight.startedAt >= freshAfter) return this.subscribe(flight, signal)
      // An older request for the same data is still running: never start a second one in
      // parallel. Wait for it (ignoring its outcome), then fetch again.
      await this.subscribe(flight, signal).catch((error) => {
        if (signal?.aborted) throw error
      })
      return this.get(key, options)
    }
    if (flight) this.cancelFlight(flight)
    return this.subscribe(this.start(key), signal)
  }

  private start(key: string): Flight<T> {
    const controller = new AbortController()
    const startedAt = this.now()
    const flight = { key, startedAt, controller, subscribers: 0, abortTimer: null } as unknown as Flight<T>
    flight.promise = this.fetcher(key, controller.signal)
      .then((value) => {
        if (!this.entry || this.entry.key !== key || this.entry.requestedAt <= startedAt) {
          this.entry = { key, value, requestedAt: startedAt }
        }
        return value
      })
      .finally(() => {
        if (this.flight === flight) this.flight = null
      })
    // Every subscriber handles the outcome; this keeps an abandoned request from being reported
    // as an unhandled rejection.
    flight.promise.catch(() => undefined)
    this.flight = flight
    return flight
  }

  private cancelFlight(flight: Flight<T>) {
    if (flight.abortTimer) clearTimeout(flight.abortTimer)
    flight.controller.abort()
    if (this.flight === flight) this.flight = null
  }

  private subscribe(flight: Flight<T>, signal?: AbortSignal): Promise<T> {
    if (flight.abortTimer) {
      clearTimeout(flight.abortTimer)
      flight.abortTimer = null
    }
    flight.subscribers += 1
    if (!signal) return flight.promise
    if (signal.aborted) {
      this.unsubscribe(flight)
      return Promise.reject(createAbortError())
    }
    return new Promise<T>((resolve, reject) => {
      const onAbort = () => {
        this.unsubscribe(flight)
        reject(createAbortError())
      }
      signal.addEventListener('abort', onAbort, { once: true })
      flight.promise.then(
        (value) => {
          signal.removeEventListener('abort', onAbort)
          resolve(value)
        },
        (error) => {
          signal.removeEventListener('abort', onAbort)
          reject(error)
        },
      )
    })
  }

  private unsubscribe(flight: Flight<T>) {
    flight.subscribers -= 1
    if (flight.subscribers > 0 || this.flight !== flight) return
    // Deferred: a caller that re-subscribes in the same tick (StrictMode remount) keeps it alive.
    flight.abortTimer = setTimeout(() => {
      flight.abortTimer = null
      if (flight.subscribers <= 0 && this.flight === flight) this.cancelFlight(flight)
    }, 0)
  }
}
